import { ApiError, type Language, type Order, type OrderFile, type OrderHistory, type OrderPart, type User } from './types'

const API_BASE = '/api'

export const sessionStorageKeys = {
  token: 'token',
  user: 'user',
} as const

export function getToken() {
  return localStorage.getItem(sessionStorageKeys.token)
}

export function mediaUrl(key: string) {
  const token = getToken()
  return `${API_BASE}/media?key=${encodeURIComponent(key)}${token ? `&token=${encodeURIComponent(token)}` : ''}`
}

function toQuery(params?: Record<string, string | number | undefined | null>) {
  if (!params) return ''
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') query.set(key, String(value))
  })
  const result = query.toString()
  return result ? `?${result}` : ''
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers)
  const token = getToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (options.body && !(options.body instanceof FormData)) headers.set('Content-Type', 'application/json')

  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, { ...options, headers })
  } catch {
    throw new ApiError('NETWORK_ERROR', 0)
  }

  const contentType = response.headers.get('content-type') || ''
  const payload = contentType.includes('application/json') ? await response.json() : await response.text()
  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem(sessionStorageKeys.token)
      localStorage.removeItem(sessionStorageKeys.user)
      window.dispatchEvent(new CustomEvent('session-expired'))
      if (location.hash !== '#/login') location.hash = '#/login'
    }
    const message = typeof payload === 'object' && payload && 'error' in payload ? String(payload.error) : response.statusText
    throw new ApiError(message || 'REQUEST_FAILED', response.status, payload)
  }
  return payload as T
}

export const api = {
  get<T>(path: string, params?: Record<string, string | number | undefined | null>) {
    return request<T>(`${path}${toQuery(params)}`)
  },
  post<T>(path: string, data?: unknown) {
    return request<T>(path, { method: 'POST', body: data instanceof FormData ? data : JSON.stringify(data ?? {}) })
  },
  put<T>(path: string, data?: unknown) {
    return request<T>(path, { method: 'PUT', body: JSON.stringify(data ?? {}) })
  },
  delete<T>(path: string, data?: unknown) {
    return request<T>(path, { method: 'DELETE', body: data === undefined ? undefined : JSON.stringify(data) })
  },
  login(username: string, password: string) {
    return request<{ token: string; user: User }>('/auth', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    })
  },
  me() {
    return request<{ user: User }>('/auth')
  },
  updateLanguage(language: Language) {
    return request<{ success: boolean }>('/auth/language', {
      method: 'PUT',
      body: JSON.stringify({ language }),
    })
  },
  orders(params?: Record<string, string | number | undefined>) {
    return request<{ orders: Order[]; total: number; page: number; pageSize: number }>(`/orders${toQuery(params)}`)
  },
  order(id: number | string) {
    return request<{ order: Order; history: OrderHistory[]; files: OrderFile[] }>(`/orders/${id}`)
  },
  createOrder(data: Record<string, unknown>) {
    return request<{ success: boolean; id: number; order_number: string }>('/orders', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
  updateOrder(id: number | string, data: Record<string, unknown>) {
    return request<{ success: boolean }>(`/orders/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  },
  orderParts(id: number | string) {
    return request<{ parts: OrderPart[] }>(`/orders/${id}/parts`)
  },
  uploadOrderFile(id: number | string, file: File) {
    const data = new FormData()
    data.append('file', file)
    return request<{ success: boolean; key: string }>(`/upload/${id}`, { method: 'POST', body: data })
  },
}

export { toQuery }
